import { Injectable, inject } from '@angular/core';
import { BaseService } from '../base';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, throwError, firstValueFrom } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { Bajas, BajasDTO } from '../../models/personal/bajas.models';
import { environment } from '../../../environments/environment';
import { ApiResponse, IApiResponse } from '../../models/base';

const BAJAS_ENDPOINT = '/bajas/';
const FILES_ENDPOINT = '/files/';

@Injectable({
  providedIn: 'root',
})
export class BajasService extends BaseService {
  loading$ = new BehaviorSubject<boolean>(false);
  bajas$ = new BehaviorSubject<BajasDTO[]>([]);
  bajasByUser$ = new BehaviorSubject<BajasDTO[]>([]);

  private bajas: BajasDTO[] = [];
  private bajasByUser: BajasDTO[] = [];

  private readonly http = inject(HttpClient);

  constructor() {
    const snackBar = inject(MatSnackBar);
    super(snackBar);
  }

  public async delete(idBaja: number): Promise<string> {
    if (idBaja <= 0) {
      return Promise.reject('El identificador de la baja debe ser mayor que cero.');
    }

    this.loading$.next(true);

    try {
      const response = await firstValueFrom(
        this.http
          .delete<IApiResponse<any>>(`${environment.apiUrl}${BAJAS_ENDPOINT}${idBaja}`)
          .pipe(
            finalize(() => {
              this.loading$.next(false);
            })
          )
      );

      if (response.error) {
        throw new Error(typeof response.error === 'string' ? response.error : response.message);
      }

      this.bajas = this.bajas.filter((baja) => baja.baja.id !== idBaja);
      this.bajas$.next(this.bajas);

      return response.message || 'Baja eliminada correctamente';
    } catch (error) {
      this.procesarErrorGeneral(error);
      throw error;
    }
  }

  public async getAllBajasByUser(idUsuario?: number): Promise<BajasDTO[]> {
    this.loading$.next(true);

    try {
      const response = await firstValueFrom(
        this.http
          .get<IApiResponse<BajasDTO[]>>(
            `${environment.apiUrl}${BAJAS_ENDPOINT}GetAllBajasByUser${
              idUsuario ? `?idUsuario=${idUsuario}` : ''
            }`
          )
          .pipe(
            finalize(() => {
              this.loading$.next(false);
            })
          )
      );

      if (response.error) {
        throw new Error(typeof response.error === 'string' ? response.error : response.message);
      }

      const bajas = (response.data || []).map((bajaDTO) => ({
        ...bajaDTO,
        documentos: bajaDTO.documentos || [],
      }));

      this.bajasByUser = bajas;
      this.bajasByUser$.next(bajas);

      return bajas;
    } catch (error) {
      this.procesarErrorGeneral(error);
      throw error;
    }
  }

  public async getActiveBajasByUser(): Promise<BajasDTO[]> {
    this.loading$.next(true);

    try {
      const response = await firstValueFrom(
        this.http
          .get<IApiResponse<BajasDTO[]>>(
            `${environment.apiUrl}${BAJAS_ENDPOINT}GetActiveBajasByUser`
          )
          .pipe(
            finalize(() => {
              this.loading$.next(false);
            })
          )
      );

      if (response.error) {
        throw new Error(typeof response.error === 'string' ? response.error : response.message);
      }

      const bajas = (response.data || []).map((bajaDTO) => ({
        ...bajaDTO,
        documentos: bajaDTO.documentos || [],
      }));

      this.bajasByUser = bajas;
      this.bajasByUser$.next(bajas);

      return bajas;
    } catch (error) {
      this.procesarErrorGeneral(error);
      throw error;
    }
  }

  public async getAllBajasByEmployee(idEmpleado: number): Promise<BajasDTO[]> {
    if (idEmpleado <= 0) {
      return Promise.reject('El identificador del empleado debe ser mayor que cero.');
    }

    this.loading$.next(true);

    this.bajas = [];
    this.bajas$.next(this.bajas);

    try {
      const response = await firstValueFrom(
        this.http
          .get<IApiResponse<BajasDTO[]>>(`${environment.apiUrl}${BAJAS_ENDPOINT}empleado/${idEmpleado}`)
          .pipe(
            finalize(() => {
              this.loading$.next(false);
            })
          )
      );

      if (response.error) {
        throw new Error(typeof response.error === 'string' ? response.error : response.message);
      }

      const bajas = (response.data || []).map((bajaDTO) => ({
        ...bajaDTO,
        documentos: bajaDTO.documentos || [],
      }));

      this.bajas = bajas;
      this.bajas$.next(bajas);

      return bajas;
    } catch (error) {
      this.procesarErrorGeneral(error);
      throw error;
    }
  }

  getBajasPorFechas(inicio: string, fin: string): Observable<Bajas[]> {
    return this.http
      .get<ApiResponse<Bajas[]>>(
        environment.apiUrl + BAJAS_ENDPOINT + 'rango/' + inicio + '/' + fin
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          throw resultado;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  public getBajas(): BajasDTO[] {
    return this.bajas;
  }

  public setBajas(bajas: BajasDTO[]): void {
    this.bajas = bajas;
    this.bajas$.next(bajas);
  }

  public newBajas(): Bajas {
    return {
      id: 0,
      employees_Id: undefined,
      inicio: '',
      fin: null,
      motivo: '',
      fechaSolicitud: '',
      users_id: 0,
      cadenaInicio: '',
      cadenaFin: '',
      cadenaFechaSolicitud: '',
    };
  }

  public async create(baja: Bajas, fileUid: number, fileGuid: string): Promise<BajasDTO> {
    this.loading$.next(true);

    const bajasDTO: BajasDTO = {
      baja,
      documentos: [],
    };

    if (fileUid) {
      bajasDTO.documentos.push({
        id: 0,
        bajas_Id: 0,
        employees_Id: baja.employees_Id,
        users_id: baja.users_id,
        path: '',
        name: `documento_baja_${Date.now()}`,
        file_Id: fileUid,
        fileGuid,
      });
    }

    try {
      const response = await firstValueFrom(
        this.http
          .post<ApiResponse<BajasDTO>>(`${environment.apiUrl}${BAJAS_ENDPOINT}`, bajasDTO)
          .pipe(
            finalize(() => {
              this.loading$.next(false);
            })
          )
      );

      if (response.error) {
        this.procesarErrorGeneral(response);
        return null;
      }

      if (response.data && response.data.error != null && response.data.error !== '') {
        this.procesarErrorGeneral(response.data);
        return null;
      }

      this.bajas.push(response.data);
      this.bajas$.next(this.bajas);

      return response.data;
    } catch (error) {
      this.procesarErrorGeneral(error);
      throw error;
    }
  }

  public async update(baja: Bajas, fileUid: number, fileGuid: string): Promise<BajasDTO> {
    if (baja.id <= 0) {
      return Promise.reject('El identificador de la baja debe ser mayor que cero.');
    }

    this.loading$.next(true);

    const bajasDTO: BajasDTO = {
      baja,
      documentos: [],
    };

    if (fileUid) {
      bajasDTO.documentos.push({
        id: 0,
        bajas_Id: baja.id,
        employees_Id: baja.employees_Id,
        users_id: baja.users_id,
        path: '',
        name: `documento_baja_${Date.now()}`,
        file_Id: fileUid,
        fileGuid,
      });
    }

    try {
      const response = await firstValueFrom(
        this.http
          .put<ApiResponse<BajasDTO>>(`${environment.apiUrl}${BAJAS_ENDPOINT}`, bajasDTO)
          .pipe(
            finalize(() => {
              this.loading$.next(false);
            })
          )
      );

      if (response.error) {
        this.procesarErrorGeneral(response);
        return null;
      }

      if (response.data && response.data.error != null && response.data.error !== '') {
        this.procesarErrorGeneral(response.data);
        return null;
      }

      this.bajas = this.bajas.map((item) =>
        item.baja.id === response.data.baja.id ? response.data : item
      );

      this.bajas$.next(this.bajas);

      return response.data;
    } catch (error) {
      this.procesarErrorGeneral(error);
      throw error;
    }
  }

  public getFile(fileGuid: string): void {
    const url = `${environment.apiUrl}${FILES_ENDPOINT}getimagefromfiles/${fileGuid}/PDF`;

    this.loading$.next(true);

    this.http
      .get(url, { responseType: 'blob' })
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe({
        next: (resultado: Blob) => {
          if (resultado) {
            const objectUrl = window.URL.createObjectURL(resultado);

            const link = document.createElement('a');
            link.href = objectUrl;
            link.target = '_blank';

            document.body.appendChild(link);
            link.click();

            URL.revokeObjectURL(objectUrl);
            document.body.removeChild(link);
          }
        },
        error: (error) => {
          this.procesarError(error);
        },
      });
  }
}